import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useEffect } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";

export interface SelectableItem {
  id: string;
  label: string;
  description?: string;
}

interface SearchableMultiSelectProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  items: SelectableItem[];
  searchPlaceholder?: string;
  label?: string;
  description?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  defaultSelectAll?: boolean;
}

export function SearchableMultiSelect<T extends FieldValues>({
  form,
  name,
  items,
  searchPlaceholder = "Suchen...",
  label,
  description,
  searchValue = "",
  onSearchChange,
  defaultSelectAll = false,
}: SearchableMultiSelectProps<T>) {
  const filteredItems = items.filter(
    (item) =>
      item?.label?.toLowerCase().includes(searchValue.toLowerCase()) ||
      item?.description?.toLowerCase().includes(searchValue.toLowerCase()),
  );

  useEffect(() => {
    if (defaultSelectAll && items.length > 0) {
      const currentValue = form.getValues(name);
      if (!currentValue || currentValue.length === 0) {
        form.setValue(name, items.map((item) => item.id) as any);
      }
    }
  }, []);

  return (
    <div className="space-y-4">
      {label && <h3 className="font-medium text-sm">{label}</h3>}
      {description && (
        <div className="text-sm text-muted-foreground">{description}</div>
      )}
      <div className="border rounded-lg p-4">
        <FormField
          control={form.control}
          name={name}
          render={({ field }) => {
            return (
              <FormItem>
                <div className="space-y-4">
                  <Input
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={(e) => onSearchChange?.(e.target.value)}
                    className="mb-2"
                    tabIndex={0}
                    type="text"
                  />

                  <div className="flex items-center space-x-2 mb-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        field.onChange(items.map((item) => item.id))
                      }
                      tabIndex={-1}
                    >
                      Alle auswählen
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => field.onChange([])}
                      tabIndex={-1}
                    >
                      Auswahl aufheben
                    </Button>
                  </div>

                  <div className="border rounded-md divide-y h-64 overflow-y-auto">
                    {filteredItems.length > 0 ? (
                      filteredItems.map((item) => (
                        <label
                          key={item.id}
                          className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer"
                          onKeyDown={(e) => {
                            // Prevent space and enter from submitting
                            if (e.key === " " || e.key === "Enter") {
                              e.preventDefault();
                            }
                          }}
                        >
                          <Checkbox
                            checked={field.value?.includes(item.id)}
                            onCheckedChange={(checked) => {
                              const newValue = checked
                                ? [...(field.value || []), item.id]
                                : (field.value || []).filter(
                                    (id) => id !== item.id,
                                  );
                              field.onChange(newValue);
                            }}
                            tabIndex={0}
                          />
                          <div className="flex-1">
                            <div className="font-medium">{item.label}</div>
                            {item.description && (
                              <div className="text-sm text-gray-500">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </label>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        Keine Ergebnisse gefunden
                      </div>
                    )}
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </div>
    </div>
  );
}
